import React, { useCallback, useMemo, useState } from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import ShowIf from '@components/Atoms/ShowIf';
import Button from '@components/Atoms/Button';

import ReviewQuestionContainersDesktop from '@components/PracticeReviewManagement/ReviewQuestionContainersDesktop';
import { useGetQuestionDetails } from 'chalktalk-react/hooks/practiceHooks';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ShadowScrollbars from '@components/Scrollbar';

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL;

const LegacyQuestionReviewer = ({
  questionId,
  examQuestionId,
  onBack,
}: {
  questionId: string;
  examQuestionId: string;
  onBack: () => void;
}) => {
  const [displayMode, setDisplayMode] = useState<'formatted' | 'raw'>('formatted');
  const { data: questionDetail, isLoading: questionIsLoading } = useGetQuestionDetails(
    Number(questionId),
  );

  const hasSharedAsset = useMemo<boolean>(
    () => (questionDetail?.exam_question?.question?.shared_assets?.length ?? 0) > 0,
    [questionDetail],
  );

  const displayJsonContent = useCallback(
    (title: string, jsonContent: object) => (
      <Box sx={{ flex: 1, height: '80vh', overflow: 'auto' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <ShadowScrollbars style={{ flex: 1, height: 'calc(80vh - 4em)' }}>
          <Box
            component="pre"
            sx={{
              padding: '1rem',
              backgroundColor: '#e3e3e3',
            }}
          >
            <Box
              component="code"
              sx={{
                fontFamily: '"Courier New", Courier, monospace',
                whiteSpace: 'pre-wrap',
                fontSize: '0.75rem',
              }}
            >
              {JSON.stringify(jsonContent ?? {}, null, 2)}
            </Box>
          </Box>
        </ShadowScrollbars>
      </Box>
    ),
    [],
  );

  return (
    <>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          px: 2,
          py: 1,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button onClick={() => onBack?.()}>Back to List</Button>
          <ShowIf If={hasSharedAsset}>
            <a
              href={`${ADMIN_URL}/practice/exam-question/${examQuestionId}/asset/edit/`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open Asset ${examQuestionId} in editor`}
            >
              Open Asset Editor
            </a>
          </ShowIf>
        </Box>
        <Typography variant="body2">ID: {questionId}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={displayMode}
            exclusive
            onChange={(_, value) => value && setDisplayMode(value as 'formatted' | 'raw')}
            aria-label="Toggle Question Display Mode"
            size="small"
            sx={{
              mr: '10em',
            }}
          >
            <ToggleButton value="formatted" aria-label="left aligned">
              Formatted
            </ToggleButton>
            <ToggleButton value="raw" aria-label="centered">
              Raw
            </ToggleButton>
          </ToggleButtonGroup>
          <a
            href={`${ADMIN_URL}/practice/question/${examQuestionId}/edit-radio/`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open question ${examQuestionId} in editor`}
          >
            Open in Question Editor
          </a>
        </Box>
      </Box>
      <ShowIf If={questionDetail && !questionIsLoading}>
        <ShowIf If={displayMode === 'formatted'}>
          <ReviewQuestionContainersDesktop
            questions={[questionDetail]}
            questionSelected={0}
            isPracticeNavigation={false}
            nextQuestion={() => undefined}
            prevQuestion={() => undefined}
            selectQuestion={() => undefined}
            openReportErrorModal={() => undefined}
            excludeFooter
          />
        </ShowIf>
        <ShowIf If={displayMode === 'raw'}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '1rem',
              justifyContent: 'space-between',
            }}
          >
            {displayJsonContent(
              'Shared Assets',
              questionDetail?.exam_question?.question?.shared_assets ?? {},
            )}
            {displayJsonContent('Prompt', {
              prompt: questionDetail?.exam_question?.question?.prompt ?? {},
              choices: questionDetail?.exam_question?.question?.choices ?? [],
            })}
            {displayJsonContent(
              'Explanation',
              questionDetail?.exam_question?.question?.explanation ?? {},
            )}
          </Box>
        </ShowIf>
      </ShowIf>
    </>
  );
};

export default LegacyQuestionReviewer;
